import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { FileSignature, Save, ExternalLink, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Field, NSelect, TInput } from "@/components/technique/fields";
import { useAuth } from "@/context/AuthContext";
import { permCan } from "@/lib/constants";

const STATUSES = ["Préparation", "En cours", "En pause", "Pré-réception", "Réceptionné", "Clôturé"];

function ContractCard({ contract, fid }) {
  const nav = useNavigate();
  const { permissions } = useAuth();
  const openPdf = async () => {
    try {
      const r = await api.get(`/technique/files/${fid}/contract-pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(apiErr(e)); }
  };
  if (!contract) return <p className="text-sm text-slate-400 p-4 rounded-lg border border-dashed">Aucun contrat rattaché à ce projet client.</p>;
  return (
    <div className="rounded-lg border bg-white p-4 space-y-2" data-testid="tech-contract-card">
      <div className="flex items-center gap-2"><FileSignature className="h-4 w-4 text-brand-600" /><span className="font-semibold text-sm">Contrat {contract.numero}</span><StatusBadge value={contract.statut} /></div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div><p className="text-[10px] uppercase font-mono text-slate-400">Client</p>{contract.client_name || "—"}</div>
        <div><p className="text-[10px] uppercase font-mono text-slate-400">Montant</p><span className="font-mono font-bold">{fmtMoney(contract.montant || 0)}</span></div>
        <div className="col-span-2"><p className="text-[10px] uppercase font-mono text-slate-400">Objet</p>{contract.titre || contract.projet || contract.client_project_name || "—"}</div>
        <div><p className="text-[10px] uppercase font-mono text-slate-400">Date</p>{contract.date ? fmtDate(contract.date) : "—"}</div>
        <div><p className="text-[10px] uppercase font-mono text-slate-400">Durée</p>{contract.duree || "—"}</div>
      </div>
      <div className="flex gap-2 pt-1 flex-wrap">
        <Button size="sm" variant="outline" onClick={openPdf} data-testid="tech-contract-pdf"><FileDown className="h-4 w-4 mr-1" />Document contractuel (PDF)</Button>
        {permCan(permissions, "contrats", "read") && <Button size="sm" variant="outline" onClick={() => nav("/contrats")} data-testid="tech-contract-view"><ExternalLink className="h-4 w-4 mr-1" />Voir le contrat</Button>}
      </div>
    </div>
  );
}

export default function TechInfo({ data, staff, onSaved }) {
  const f = data.file;
  const [form, setForm] = useState({ contract_id: f.contract_id, statut: f.statut, responsable_id: f.responsable_id, conducteur_id: f.conducteur_id,
    chantier_nom: f.chantier_nom, chantier_adresse: f.chantier_adresse, date_demarrage: f.date_demarrage, date_fin_prevue: f.date_fin_prevue, notes: f.notes });
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const users = staff.map((u) => ({ value: u.id, label: u.name }));
  const save = async () => {
    try { await api.put(`/technique/files/${f.id}`, form); toast.success("Dossier mis à jour"); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="grid lg:grid-cols-5 gap-4">
      <div className="lg:col-span-3 rounded-lg border bg-white p-4 grid sm:grid-cols-2 gap-3">
        <Field label="Statut technique"><NSelect value={form.statut} onChange={set("statut")} options={STATUSES} testid="tech-f-statut" /></Field>
        <Field label="Contrat rattaché"><NSelect disabled={!data.supervisor} value={form.contract_id} onChange={set("contract_id")} placeholder="Aucun" options={data.contracts.map((c) => ({ value: c.id, label: `${c.numero} · ${fmtMoney(c.montant || 0)}` }))} testid="tech-f-contract" /></Field>
        <Field label="Responsable technique"><NSelect disabled={!data.supervisor} value={form.responsable_id} onChange={set("responsable_id")} options={users} testid="tech-f-responsable" /></Field>
        <Field label="Conducteur de travaux"><NSelect disabled={!data.supervisor} value={form.conducteur_id} onChange={set("conducteur_id")} options={users} testid="tech-f-conducteur" /></Field>
        <Field label="Chantier"><TInput value={form.chantier_nom} onChange={set("chantier_nom")} testid="tech-f-chantier" /></Field>
        <Field label="Adresse du chantier"><TInput value={form.chantier_adresse} onChange={set("chantier_adresse")} testid="tech-f-adresse" /></Field>
        <Field label="Date de démarrage"><TInput type="date" value={form.date_demarrage} onChange={set("date_demarrage")} testid="tech-f-debut" /></Field>
        <Field label="Date de fin prévue"><TInput type="date" value={form.date_fin_prevue} onChange={set("date_fin_prevue")} testid="tech-f-fin" /></Field>
        <Field label="Notes" className="sm:col-span-2"><Textarea value={form.notes || ""} onChange={(e) => set("notes")(e.target.value)} rows={2} data-testid="tech-f-notes" /></Field>
        <div className="sm:col-span-2 flex justify-end"><Button onClick={save} data-testid="tech-f-save" className="bg-brand-600 hover:bg-brand-700 text-white"><Save className="h-4 w-4 mr-1.5" />Enregistrer</Button></div>
      </div>
      <div className="lg:col-span-2 space-y-3">
        <div className="rounded-lg border bg-slate-50 p-4 text-sm space-y-1" data-testid="tech-project-card">
          <p className="text-[10px] uppercase font-mono text-slate-400">Projet client</p>
          <p className="font-semibold">{data.project?.numero} · {data.project?.nom}</p>
          <p className="text-slate-600">Client : {f.client_name || "—"}</p>
          <p className="text-slate-600">Budget : <span className="font-mono">{fmtMoney(data.project?.budget || 0)}</span></p>
        </div>
        <ContractCard contract={data.contract} fid={f.id} />
      </div>
    </div>
  );
}
